import { useEffect, useState } from 'react';

const getILRUrl = (ecgId: string) =>
  `/api/ecg/${ecgId}/report/plot/resting.png`;

interface Image {
  url: string;
  data?: string;
  status: number;
}

interface ImageCache {
  url: string;
  image: Promise<Image>;
}

let cache: ImageCache | null = null;

export const getImage = async (ecgId: string): Promise<Image> => {
  const url = getILRUrl(ecgId);

  if (cache?.url === url) {
    return cache.image;
  }

  cache = {
    url,
    image: loadImage(url),
  };

  return cache.image;
};

const loadImage = async (url: string) => {
  const response = await fetch(url);
  const blob = await response.blob();

  return new Promise<Image>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const data = reader.result?.toString();
      const image: Image = { status: response.status, data, url };
      resolve(image);
    };
    reader.readAsDataURL(blob);
  });
};

export const hasILRImage = (image?: Image): image is Image => {
  return image?.data != null && image.status !== 204;
};

export const useILRImage = (ecgId: string) => {
  const [image, setImage] = useState<Image>();

  useEffect(() => {
    let reRendered = false;
    (async () => {
      const img = await getImage(ecgId);
      if (!reRendered) {
        setImage(img);
      }
    })();
    return () => {
      reRendered = true;
    };
  }, [ecgId]);

  return image;
};
